import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ORG_TYPES, type Org, type OrgInput, type OrgStatus } from "../lib/types";
import { Field, btnPrimary, inputCls } from "./ui";

/** 学校の入力欄一式（新規・編集で共通。data-testid は spec 12章） */
export function OrgForm({
  initial,
  submitting,
  submitLabel,
  onSubmit,
}: {
  initial?: Org | null;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (v: OrgInput) => void;
}) {
  const { t } = useTranslation(["common", "admin"]);
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState<string>(initial?.type && ORG_TYPES.includes(initial.type as never) ? initial.type : "university");
  const [starts, setStarts] = useState(initial?.contract_starts_on ?? "");
  const [ends, setEnds] = useState(initial?.contract_ends_on ?? "");
  const [cap, setCap] = useState(initial?.learner_cap == null ? "" : String(initial.learner_cap));
  const [status, setStatus] = useState<OrgStatus>(initial?.status === "suspended" ? "suspended" : "active");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const capNum = cap.trim() === "" ? null : Math.max(0, Math.floor(Number(cap)));
    onSubmit({
      name: name.trim(),
      type,
      status,
      contract_starts_on: starts || null,
      contract_ends_on: ends || null,
      learner_cap: capNum == null || Number.isNaN(capNum) ? null : capNum,
    });
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <Field label={t("admin:org_name")}>
          <input data-testid="org-form-name" type="text" required maxLength={120} value={name}
            onChange={(e) => setName(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <Field label={t("admin:org_type")}>
        <select data-testid="org-form-type" value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
          {ORG_TYPES.map((v) => <option key={v} value={v}>{t(`admin:type_${v}`)}</option>)}
        </select>
      </Field>
      <Field label={t("admin:org_status")}>
        <select data-testid="org-form-status" value={status} onChange={(e) => setStatus(e.target.value as OrgStatus)} className={inputCls}>
          <option value="active">{t("common:status_active")}</option>
          <option value="suspended">{t("common:status_suspended")}</option>
        </select>
      </Field>
      <Field label={t("admin:org_starts")} hint={t("admin:org_date_hint")}>
        <input data-testid="org-form-starts" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} className={inputCls} />
      </Field>
      <Field label={t("admin:org_ends")} hint={t("admin:org_date_hint")}>
        <input data-testid="org-form-ends" type="date" value={ends} onChange={(e) => setEnds(e.target.value)} className={inputCls} />
      </Field>
      <Field label={t("admin:org_cap")} hint={t("admin:org_cap_hint")}>
        <input data-testid="org-form-cap" type="number" min={0} step={1} inputMode="numeric" value={cap}
          onChange={(e) => setCap(e.target.value)} className={inputCls} />
      </Field>
      <div className="sm:col-span-2 flex justify-end">
        <button data-testid="org-form-submit" type="submit" disabled={submitting} className={btnPrimary}>
          {submitting ? t("common:loading") : submitLabel}
        </button>
      </div>
    </form>
  );
}
